import { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "context/AuthContext";
import { Ring } from "@uiball/loaders";
import { seatStatusAsync, ISeatStatus } from "../../services/crystal";
import styles from "./index.module.css";

type Phase = "polling" | "done" | "billing" | "canceled" | "timeout" | "no_grant";

const CrystalSuccess: FC = () => {
  const navigate = useNavigate();
  const authInfo = useContext(AuthContext);

  const [searchParams] = useSearchParams();
  // Read the grant id from the query string (added by the server on success_url).
  const seatGrantId = searchParams.get("seat_grant_id");

  const [phase, setPhase] = useState<Phase>(seatGrantId ? "polling" : "no_grant");
  const [seat, setSeat] = useState<ISeatStatus | null>(null);

  useEffect(() => {
    if (!seatGrantId) return;

    let cancelled = false;
    // Poll about once a second. A seat that stays "preparing" is paid for and
    // safe, so a long wait is a soft "still preparing", never a hard error.
    const MAX_TRIES = 90;
    const INTERVAL_MS = 1000;

    (async () => {
      for (let i = 0; i < MAX_TRIES && !cancelled; i++) {
        try {
          const s = await seatStatusAsync(seatGrantId);
          if (cancelled) return;
          setSeat(s);
          const quantity = s.quantity ?? 1;
          if (
            s.status === "active" &&
            s.msp_id &&
            s.subject_ids.length >= quantity
          ) {
            setPhase("done");
            return;
          }
          if (s.status === "past_due" || s.status === "unpaid") {
            setPhase("billing");
            return;
          }
          if (s.status === "canceled") {
            setPhase("canceled");
            return;
          }
          // null / active-but-not-yet-placed / not-yet-bound: keep polling
        } catch (err) {
          console.error(err);
        }
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }
      if (!cancelled) setPhase("timeout");
    })();

    return () => {
      cancelled = true;
    };
  }, [seatGrantId]);

  // Where "Start Provisioning" sends the customer: into that instance's
  // provisioning page (Part 2 lives inside the MSP instance). The route is keyed
  // by the box id — subject_ids are the crystal boxes bound to this seat.
  const boxId = seat?.subject_ids?.[0];
  const provisioningUrl =
    seat?.instance && boxId
      ? `https://${seat.instance}/provisioning/${boxId}`
      : null;

  if (phase === "polling") {
    return (
      <div className={styles["container"]}>
        <div
          className={styles["content"]}
          style={{ gap: 24, justifyContent: "center", minHeight: 320 }}
        >
          <Ring size={60} />
          <h1 className={styles["headline"]}>Preparing your seat…</h1>
          <p className={styles["subtitle"]}>
            Payment received. We're setting up your Crystal seat — this usually
            takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "billing") {
    return (
      <div className={styles["container"]}>
        <div className={styles["content"]}>
          <h1 className={styles["headline"]}>Payment needs attention</h1>
          <p className={styles["subtitle"]}>
            Your Crystal seat is set up but its payment hasn't gone through yet.
            Your seat keeps working while this is sorted out. Please update your
            payment method to keep it active.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "canceled") {
    return (
      <div className={styles["container"]}>
        <div className={styles["content"]}>
          <h1 className={styles["headline"]}>This seat has been canceled</h1>
          <p className={styles["subtitle"]}>
            The entitlement for this seat has ended. If this is unexpected,
            please start a new purchase.
          </p>
          <button className={styles["start-btn"]} onClick={() => navigate("/crystal")}>
            Buy a Crystal Seat
          </button>
        </div>
      </div>
    );
  }

  if (phase === "timeout") {
    return (
      <div className={styles["container"]}>
        <div className={styles["content"]}>
          <h1 className={styles["headline"]}>Still preparing your seat</h1>
          <p className={styles["subtitle"]}>
            Your payment went through and your seat is safe. It's taking a little
            longer than usual to finish setting up. You can safely leave this
            page and check back shortly.
          </p>
          <button
            className={styles["start-btn"]}
            onClick={() => navigate("/crystal-licenses")}
          >
            View My Crystal Licenses
          </button>
        </div>
      </div>
    );
  }

  if (phase === "no_grant") {
    return (
      <div className={styles["container"]}>
        <div className={styles["content"]}>
          <h1 className={styles["headline"]}>Firewalla Crystal</h1>
          <p className={styles["subtitle"]}>
            We couldn't find a seat to set up. If you just completed a purchase,
            please check your Crystal licenses, or start a new purchase.
          </p>
          <button
            className={styles["start-btn"]}
            onClick={() => navigate("/crystal-licenses")}
          >
            View My Crystal Licenses
          </button>
        </div>
      </div>
    );
  }

  // phase === "done"
  return (
    <div className={styles["container"]}>
      <div className={styles["content"]}>
        <h1 className={styles["headline"]}>Firewalla Crystal is Ready to Go</h1>
        <p className={styles["subtitle"]}>
          Deploy Firewalla Crystal on any hardware. Simply follow the
          installation guide below to set up your device and secure your network.
        </p>
        {provisioningUrl ? (
          <button
            className={styles["start-btn"]}
            onClick={() => {
              window.location.assign(provisioningUrl);
            }}
          >
            Start Provisioning
          </button>
        ) : (
          <button
            className={styles["start-btn"]}
            onClick={() => navigate("/crystal-licenses")}
          >
            Start Provisioning
          </button>
        )}
        <div className={styles["illustration"]}>
          <img src="/images/checkout-success.svg" alt="Firewalla Crystal" />
        </div>
        <p className={styles["more-info"]}>
          We'll send an email to{" "}
          <span className={styles["email"]}>
            {authInfo.email || "your email"}
          </span>{" "}
          with a link to your account once everything is set up. In the
          meantime, feel free to check out{" "}
          <a
            href="https://help.firewalla.com/hc/en-us/articles/4409866753427"
            target="_blank"
            rel="noreferrer"
          >
            our article
          </a>{" "}
          on Firewalla MSP's key features and capabilities.
        </p>
      </div>
    </div>
  );
};

export default CrystalSuccess;
